<template>

<div class="stat">

	<span class="stat-figure">

		<!-- a rate is drawn as a thin ring gauge that fills from 0 to the value
		     when the card scrolls into view; a count / decimal has no ring -->
		<svg v-if="ring" class="stat-ring" viewBox="0 0 40 40" aria-hidden="true">
			<circle class="stat-ring-track" cx="20" cy="20" r="16.5" />
			<circle class="stat-ring-arc" cx="20" cy="20" r="16.5" :style="arcStyle" />
		</svg>

		<span class="stat-value">{{ display }}<span v-if="suffix" class="stat-suffix">{{ suffix }}</span></span>

	</span>

	<span class="stat-label">{{ label }}</span>

</div>

</template>

<script>

/*
	One archive-card metric: a figure (a count, a decimal, or a ring-gauge for a
	percentage) over a small mono label. Percentages animate from 0 to the value
	the first time the card enters the viewport — a quiet scroll reveal, skipped
	under prefers-reduced-motion. The gauge-vs-numeral contrast is the signal:
	a ring means it's a rate (solved / one-shot), a bare numeral a count (played)
	or an average (guesses).
*/

var R = 16.5
var CIRC = 2 * Math.PI * R

export default {

	name: 'ArchiveStat',

	props: {
		// the numeric value: a percentage (0..100) when ring/suffix '%', else a
		// raw count or a one-decimal average
		value: {
			default: 0
		},
		label: {
			type: String,
			default: ''
		},
		// shown after the number (e.g. '%'); also marks the value as a percentage
		suffix: {
			type: String,
			default: ''
		},
		// draw the animated ring gauge (percentages only)
		ring: {
			type: Boolean,
			default: false
		},
		// the ring's progress colour
		color: {
			type: String,
			default: 'var(--ink)'
		}
	},

	data: function() {

		return {
			// becomes true once the card is seen, releasing the fill animation
			shown: false
		}

	},

	computed: {

		display: function() {

			var v = Number(this.value) || 0

			// a percentage shows as a whole number; a decimal average keeps its
			// single place (the value is already e.g. 2.6)
			return (this.suffix === '%') ? Math.round(v) : v

		},

		pct: function() {

			return Math.max(0, Math.min(100, Number(this.value) || 0))

		},

		arcStyle: function() {

			// empty = full offset; filled = the slice that represents the percent
			var offset = this.shown ? (CIRC * (1 - this.pct / 100)) : CIRC

			return {
				stroke: this.color,
				strokeDasharray: CIRC,
				strokeDashoffset: offset
			}

		}

	},

	mounted: function() {

		var self = this

		// non-ring figures have nothing to animate
		if (!this.ring) { this.shown = true; return }

		// reduced motion (or no observer support): show the final fill at once
		var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

		if (reduce || typeof IntersectionObserver === 'undefined') { this.shown = true; return }

		// fill when the card scrolls into view, then stop observing
		this.io = new IntersectionObserver(function(entries) {

			entries.forEach(function(entry) {

				if (entry.isIntersecting) {

					self.shown = true
					if (self.io) self.io.disconnect()

				}

			})

		}, { threshold: 0.4 })

		this.io.observe(this.$el)

	},

	beforeDestroy: function() {

		if (this.io) this.io.disconnect()

	}

}

</script>

<style scoped>

.stat {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 5px;
}

.stat-figure {
	position: relative;
	width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex: none;
}

.stat-ring {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	transform: rotate(-90deg); /* begin the arc at 12 o'clock */
}

.stat-ring-track {
	fill: none;
	stroke: var(--hairline);
	stroke-width: 3;
}

.stat-ring-arc {
	fill: none;
	stroke-width: 3;
	stroke-linecap: round;
	transition: stroke-dashoffset 900ms cubic-bezier(0.22, 1, 0.36, 1);
}

.stat-value {
	position: relative;
	font-family: var(--font-mono);
	font-size: 12px;
	font-weight: 600;
	color: var(--ink);
	line-height: 1;
}

.stat-suffix {
	font-size: 8px;
	margin-left: 0.5px;
	color: var(--muted);
}

.stat-label {
	font-family: var(--font-mono);
	font-size: 8px;
	letter-spacing: 0.07em;
	text-transform: uppercase;
	color: var(--muted);
	text-align: center;
	line-height: 1.15;
}

@media (prefers-reduced-motion: reduce) {

	.stat-ring-arc {
		transition: none;
	}

}

</style>
